import { action, makeObservable, observable } from 'mobx';
import samplesModel from '../SamplesModel';
import audioPlayerModel from '../../AudioPlayer/AudioPlayerModel';
import { apiSwagger } from '../../../shared/api';
import SamplesFinder from './SamplesFinder';
import samplesDiagnosticPopupModel from '../SamplesDiagnosticPopup/SamplesDiagnosticPopupModel';
import type { Category, Sample } from '@/sharedTypes/Api';
import { getStores } from '@/stores/stores';
import { isAdminPage, isDebugPage, rightLanguage } from '@/sharedTypes/helpers';
import { triggerEvent } from '@/sharedTypes/pureHelpers';
import { safeWindow, registerGlobalInstance } from '@/sharedTypes/browserUtils';
import { ManagerEntity } from '@/shared/globalFunctions';
import MapperEntity from '@/entities/Mapper/Mapper';
import toast from '@/components/ui/custom-toast';
import { t } from '@/shared/i18n';
import { FavoriteItem } from '@/types/favorites';
import etaLoader from '@/lib/EtaLoader';

export class SamplesSelectorModel {
  get category(): Category | null {
    if (this.selectedSample) {
      return rightLanguage(this.selectedSample.category);
    } else {
      return null;
    }
  }

  get title(): string | null {
    if (this.selectedSample) {
      if (this.selectedSample.metadata.title) {
        return rightLanguage(this.selectedSample.metadata.title);
      }
    }
    return null;
  }

  get categories(): Category[] {
    const categories: Category[] = [];
    for (const sample of this.samples) {
      const category: Category = sample.category;
      if (category && !categories.find(cat => cat._id === category._id)) {
        categories.push(category);
      }
    }
    return categories;
  }

  samples: Sample[] = [];

  samplesFinder: SamplesFinder | null = null;

  // there will be exact version like 2.1.3, after fetch.
  private coreVersion: string | null = null;

  // Control samples menu open/close state
  samplesMenuOpen: boolean | null = null; // null = no change, true = open, false = close

  applySearch(query: string, areFavoritesShown?: boolean, favoriteItems?: FavoriteItem[]) {
    if (!this.samplesFinder) {}
    let samplesToSearch = this.samples;
    
    if (areFavoritesShown && favoriteItems) {
      const favoriteIdsArray = favoriteItems.map(f => f.id.toString());
      samplesToSearch = this.samples.filter(sample =>
        favoriteIdsArray.includes(sample.identifier.toString())
      );
    }
    this.samplesFinder = new SamplesFinder(samplesToSearch);
    return this.samplesFinder.search(query);
  }

  selectedSample: Sample | null = null;

  constructor() {
    makeObservable(this, {
      samples: observable,
      selectedSample: observable,
      samplesMenuOpen: observable,
      selectSample: action,
      refreshSamples: action,
      setSamplesMenuOpen: action,
    });
  }

  setSamplesMenuOpen(open: boolean | null) {
    this.samplesMenuOpen = open;
  }
  async refreshSamples() {
    const startTime = performance.now();
    console.log('Refresh samples');
    
    let progress = 0;
    let timer: any;
    if (typeof window !== 'undefined' && etaLoader) {
      etaLoader.setStage('preparingMusicEngine', progress);
      timer = setInterval(() => {
        progress = Math.min(progress + 0.005, 0.95);
        etaLoader.setStage('preparingMusicEngine', progress);
      }, 50);
      etaLoader.setContext('refreshSamples');
      etaLoader.setStage('refreshSamples_start', 0);
    }
    const showAll = isAdminPage() || isDebugPage();
    const response = await apiSwagger.samples.samplesList({ showAll });
    if(typeof window !== 'undefined' && etaLoader) {
      etaLoader.setStage('refreshSamples_start', 0.5);
    }
    const data: any = await response.json();
    if (typeof window !== 'undefined' && etaLoader) {
      clearInterval(timer);
      etaLoader.setStage('preparingMusicEngine', 1);
      etaLoader.setStage('refreshSamples_start', 0.7);
    }

    this.samples = [];
    etaLoader.setStage('refreshSamples_start', 0.9);
  
    if (data && !data.error && data.allSamples && data.allSamples.length > 0) {
        etaLoader.setStage('refreshSamples_start', 1);
        etaLoader.setStage('refreshSamples_done', 0);
        
        // Backend handles filtering
        this.samples = data.allSamples;
        
        etaLoader.setStage('refreshSamples_done', 0.4);
        triggerEvent('samplesRefreshed');
        triggerEvent('samplesRefreshed_playwright');
        etaLoader.setStage('refreshSamples_done', 0.6);
        
        const interval = setInterval(() => {
          triggerEvent('samplesRefreshed_playwright');
        }, 200);
        setTimeout(() => {
          clearInterval(interval);
        }, 560000);
        
        etaLoader.setStage('refreshSamples_done', 0.8);
        console.log('Успешно: Сэмплы обновлены');
    } else {
      toast({
        title: t('error'),
        description: t('samples.refresh_failed'),
        type: 'error',
      });
      console.error(`Failed to get samples from response ${data.error}`);
    }
    
    if(typeof window !== 'undefined' && etaLoader) {
      etaLoader.setStage('refreshSamples_done', 1);
    }
    const duration = performance.now() - startTime;
    console.log(`[Performance] refreshSamples took ${duration.toFixed(2)}ms`);
  }

  async triggerSampleSelection(sampleIdentifier: string) {
    const isAdmin = isAdminPage();
    const isDebug = isDebugPage();
    
    if (!isAdmin && !isDebug) {
      const url = safeWindow.location.getHref();
      const pathname = safeWindow.location.getPathname();
      const isRussianRoute = pathname.startsWith('/ru/');

      if (url.includes('play')) {
        const playUrl = isRussianRoute ? `/ru/play?m=${sampleIdentifier}` : `/play?m=${sampleIdentifier}`;
        safeWindow.location.pushState(null, '', playUrl);
      } else {
        const practiceUrl = isRussianRoute ? `/ru/practice?m=${sampleIdentifier}` : `/practice?m=${sampleIdentifier}`;
        safeWindow.location.pushState(null, '', practiceUrl);
      }
    } else {
      samplesSelectorModel.selectSample(sampleIdentifier);
    }
  }
  // example of filename: default/samplename.musicxml
  async selectSample(sampleIdentifier: string) {
    try {
      if (sampleIdentifier) {
        if (typeof window !== 'undefined' && etaLoader) {
          etaLoader.setContext('loadXML')
          etaLoader.setStage('loadXML_start', 0);
        }
      // Fetch initial version of core from server if we don't have one yet
      if (this.coreVersion === null) {
      try {
        if (typeof window !== 'undefined' && etaLoader) {
          etaLoader.setStage('almostReady', 0);
        }
        
          const response = await fetch('/api/coreUpdated');
          if (response.ok) {
            const serverConfig = await response.json();
            this.coreVersion = serverConfig.version;
            console.log(`Initial core version loaded: ${this.coreVersion}`);
          }
        } catch (error) {
          console.warn('Failed to fetch initial core version:', error);
        }
      } 

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('sample', sampleIdentifier);
      }
      // safeWindow.spinner.show('Loading musicsheet...');
      if (typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('loadXML_start', 0.3);
      }
      samplesDiagnosticPopupModel.startMessageListening();
      const response = await apiSwagger.samples.samplesDetail(sampleIdentifier);
      if (response.ok) {
        const data = await response.json();

        // Fetch MusicXML from CDN URL
        let encryptedMusicxml = '';
        let cache: string | undefined = undefined;

        if (data.musicxmlUrl) {
          const musicxmlResponse = await fetch(data.musicxmlUrl);
          const musicxmlEncryptedText = await musicxmlResponse.text();
          // We should not DECRYPT musicxml, we should pass it to verovio ENCRPYTED
          encryptedMusicxml = musicxmlEncryptedText;
        }

        // Fetch cache from CDN URL if available
        if (data.cacheUrl) {
          const cacheResponse = await fetch(data.cacheUrl);
          const cacheText = await cacheResponse.text();
          cache = data.encrypted ? ManagerEntity.decryptText(cacheText) : cacheText;
        } else {
          console.warn('Предупреждение: Кеш отсутствует');
        }

        const sample: Sample = data.sample;
        this.selectedSample = sample;
        console.log(" --- Selected sample:", this.selectedSample);
        if (typeof window !== 'undefined' && etaLoader) {
          etaLoader.setStage('almostReady', 0.5);
        }
        if (typeof window !== 'undefined' && etaLoader) {
          etaLoader.setStage('loadXML_start', 0.6);
        }
        let author = sample.metadata?.author;
        if (!author) {
          // Create a new author with dummy fields
          const createRes = await apiSwagger.authors.authorsCreate({
            en: 'Unknown Author',
            ru: 'Неизвестный автор',
          });
          const createdAuthorData = await createRes.json();
          author = createdAuthorData.data;
        }

        if (typeof window !== 'undefined' && etaLoader) {
          etaLoader.setStage('loadXML_start', 0.8);
        }
        // load audio;
        const audiopath = sample.audioPath;
        if (audiopath) {
          try {
            const blob = await this.getAudio(sample);
            if (blob) {
              audioPlayerModel.loadBlob(blob);
            }
          } catch (audioError) {
            throw audioError; // Re-throw to be caught by outer catch
          }
        }
        if (typeof window !== 'undefined' && etaLoader) {
          etaLoader.setStage('loadXML_start', 1);
          etaLoader.setStage('loadXML_done', 0);
        }
        
        if (
          ManagerEntity.player?.eventsRecorder.debugData &&
          Object.keys(ManagerEntity.player.eventsRecorder.debugData.events)
            .length > 0
        ) {
          await apiSwagger.debugEvents.autosaveCreate(
            ManagerEntity.player?.eventsRecorder.debugData as any
          );
          ManagerEntity.player.eventsRecorder.reset();
        }
        
        // start to record new events
        // Check if this is the same sample and prepare gamification context
        const { gamificationStore } = getStores(false, {});
        const isSameSample = this.selectedSample?.identifier === sampleIdentifier;
        const shouldRestoreProgress = isSameSample && gamificationStore.saveProgressEnabled;
        const gamificationContext = {
          shouldRestoreProgress,
          // Only pass lastMeasureIndex if we're actually restoring progress, otherwise start from 0
          lastMeasureIndex: shouldRestoreProgress ? gamificationStore.gameState.lastMeasureIndex : 0,
          sampleId: sampleIdentifier
        };
        
        console.log('[DEBUG] SamplesSelectorModel - gamification context:', gamificationContext);
        
        await ManagerEntity.setMusicxml(
          encryptedMusicxml,
          false,
          cache,
          sample.notesCustomAttributes,
          true,
          gamificationContext
        );

        samplesModel.setSampleInfo({
          author,
          title: sample.metadata?.title || { en: '', ru: '' },
        });

        if (typeof window !== 'undefined' && etaLoader) {
          etaLoader.setStage('loadXML_done', 0.3);
        }
        if (isAdminPage()) {
          MapperEntity.refreshXmlObject(ManagerEntity.decryptMusicxml(encryptedMusicxml));
        }

        if (ManagerEntity.player && ManagerEntity.player.selectedPracticeMode) {
          ManagerEntity.player.eventsRecorder.startRecording(
            ManagerEntity.player.selectedPracticeMode.id,
            sampleIdentifier
          );
        }
        if (typeof window !== 'undefined' && etaLoader) {
          etaLoader.setStage('almostReady', 0.85);
        }
        if (typeof window !== 'undefined' && etaLoader) {
          etaLoader.setStage('loadXML_done', 0.5);
        }
        // Check for new version of core from server
        try {
           const response = await fetch('/api/coreUpdated');
          // if (typeof window !== 'undefined' && etaLoader) {
          //   etaLoader.setStage('loadXML_done', 0);
          // }
           if (response.ok) {
             const serverConfig = await response.json();
             
             // If we don't have a stored version, store the current one
             if (serverConfig.version !== this.coreVersion) {
               console.log(`New version detected: ${serverConfig.version} (current: ${this.coreVersion})`);
               console.log("Reloading window due to version update...");
               safeWindow.location.reload();
               return; // Early return since we're reloading
             }
           }
         } catch (error) {
           console.warn('Failed to check for version updates:', error);
         }
        if (typeof window !== 'undefined' && etaLoader) {
          etaLoader.setStage('loadXML_done', 0.6);
        }
      } else {
        // Try to get detailed error information from the response
        let errorMessage = 'Failed to load sample';
        try {
          const errorData = await response.json();
          if (errorData.msg) {
            errorMessage = errorData.msg;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch {
          // If JSON parsing fails, use the status text
          errorMessage = response.statusText || `HTTP ${response.status}`;
        }
        if (typeof window !== 'undefined' && etaLoader) {
          etaLoader.setStage('loadXML_done', 0.7);
        }
        toast({
          title: t('error'),
          description: t('samples.load_failed'),
          type: 'error',
        });

        // Throw error with detailed description
        throw new Error(`Sample loading failed: ${errorMessage} (Status: ${response.status})`);
      }
      // safeWindow.spinner.hide();
      if (typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('almostReady', 1);
      }
      if (typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('loadXML_done', 1);
      }
      
      // Clear any previous error state on successful load
      if (typeof window !== 'undefined' && etaLoader && etaLoader.hasError) {
        etaLoader.clearError();
      }
    } else {
      console.error('Ошибка: У сэмпла нет идентификатора');
    }
    samplesDiagnosticPopupModel.stopMessageListening();
    } catch (error: any) {
      // Stop diagnostic popup listening
      samplesDiagnosticPopupModel.stopMessageListening();
      
      // Complete all stages to reach 100% before showing error
      // Need to complete both global and loadXML contexts in case error happens during initial load
      if (typeof window !== 'undefined' && etaLoader) {
        // Complete global context stages if they exist
        if (etaLoader.context === 'global') {
          etaLoader.setStage('startingUp', 1);
          etaLoader.setStage('initializingAccount', 1);
          etaLoader.setStage('preparingMusicEngine', 1);
          etaLoader.setStage('almostReady', 1);
        }
        // Complete loadXML context stages
        etaLoader.setStage('loadXML_start', 1);
        etaLoader.setStage('loadXML_done', 1);
      }
      // Store the sample identifier that failed so we can check later if it was retried successfully
      const failedSampleId = sampleIdentifier;
      
      // Clear the selected sample to prevent re-loading the same failed sample
      this.selectedSample = null;
      
      // Close the samples selector popover immediately
      this.samplesMenuOpen = false;

      // Wait for loader animation to complete before showing error
      const errorMessage = t('samples.load_error_dialog.description');
      setTimeout(() => {
        // Only show error if the sample is still not loaded (i.e., no retry succeeded)
        // This prevents showing error when React Strict Mode causes multiple mount/unmount cycles
        // and a later attempt succeeds
        if (!this.selectedSample || this.selectedSample.identifier !== failedSampleId) {
          // Show error page with action to open samples menu when user clicks button
          etaLoader.showError(errorMessage, () => {
            // This runs when user clicks "Choose Another Piece" button
            this.samplesMenuOpen = true;
          });
        } else {
          console.log('Skipping error display - sample was loaded successfully by a retry');
        }
      }, 1300); // Wait for the loader to animate to 100% and fade out

      // Log the error
      console.error('Sample loading error:', error);
    }
  }

  async getAudio(sample: any) {
    const response = await apiSwagger.samples.audioDetail(sample._id);
    if (response.ok) {
      const data = await response.json();

      // Fetch audio from CDN URL
      if (data.audioUrl) {
        const audioResponse = await fetch(data.audioUrl);
        
        // Check if the CDN fetch was successful
        if (!audioResponse.ok) {
          console.error(`Failed to fetch audio from CDN: ${audioResponse.status} ${audioResponse.statusText}`);
          throw new Error(`Audio file not found (${audioResponse.status})`);
        }
        
        const audioBase64 = await audioResponse.text();

        // Decrypt if encrypted
        const audioData = data.encrypted
          ? ManagerEntity.decryptBinary(audioBase64)
          : Buffer.from(audioBase64, 'base64');

        // Convert to Blob
        const blobData = new Blob([audioData], { type: 'audio/mpeg' });
        return blobData;
      }

      return null;
    } else {
      toast({
        title: t('error'),
        description: t('samples.audio_load_failed'),
        type: 'error',
      });
      console.error(
        `Ошибка: Ошибка при получении аудио для сэмпла: ${rightLanguage(
          sample.metadata.title
        )}`
      );
    }
    return null;
  }

  async getMidi(sample: any) {
    const response = await apiSwagger.samples.midiDetail(sample._id);
    if (response.ok) {
      const data = await response.json();

      // Fetch MIDI from CDN URL
      if (data.midiUrl) {
        const midiResponse = await fetch(data.midiUrl);
        const midiBase64 = await midiResponse.text();

        // Decrypt if encrypted
        const midiData = data.encrypted
          ? ManagerEntity.decryptBinary(midiBase64)
          : Buffer.from(midiBase64, 'base64');

        // Convert to ArrayBuffer
        return midiData.buffer;
      }

      return null;
    }
    return null;
  }

  private samplesCache: Sample[] | null = null;
  async selectFavorites(favorites: FavoriteItem[]): Promise<Sample[]> {
    if (favorites.length === 0) return [];

    // refresh only if cache is empty
    if (!this.samplesCache || this.samplesCache.length === 0) {
      await this.refreshSamples();
      this.samplesCache = this.samples; // cache the samples
    }

    const favoriteIdsSet = new Set(favorites.map(fav => fav.id.toString()));

    return this.samplesCache.filter(sample => favoriteIdsSet.has(sample.identifier.toString()));
  }
}

const samplesSelectorModel = new SamplesSelectorModel();
registerGlobalInstance('samplesSelectorModel', samplesSelectorModel);
export default samplesSelectorModel;
